/**
 * 마이페이지 (Phase 5-2) — 본인 계정 관리.
 *
 * 7섹션:
 *   ① 본인 정보 (userId / 이름 / 권한 / 보안 등급)
 *   ② 결재 서명 (사인·직인) — 서명/직인 이미지 등록 (판매보고서 결재판에 사용, v1.5.0)
 *   ②-1 메일 계정 — 네이버웍스 개인 계정 연결 (문서 메일 발송용, v2.108.0 · v2.250.0 부터 viewer 포함 전 직원)
 *   ②-1-1 메일 꼬리말 — 보내는 메일마다 본문 끝에 붙는 인사말·연락처 (v2.125.0 · v2.250.0 부터 viewer 포함 전 직원)
 *   ②-2 NAS 계정 — 본인 NAS 자격증명 연결 (파일 탐색기용 · 모든 권한)
 *   ③ 비밀번호 변경
 *   ④ 2단계 인증 (OTP) — 미등록: 등록 / 등록됨: 해제
 *   ⑤ 신뢰된 디바이스 — 목록 + 행 단위 해제
 *
 * 권한: 모든 권한 (본인 계정 관리는 누구나 가능). 사이드바 하단 사용자 박스 클릭으로 진입.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as authApi from '../lib/auth-api';
import * as mailApi from '../lib/mail-api';
import * as calApi from '../lib/calendar-api';
import { connectNasAccount, disconnectNasAccount, fetchNasAccount } from '../lib/nas-api';
import { useAuth } from '../lib/auth-context';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { RichEditor } from '../components/RichEditor';
import {
  EMPTY_DOC,
  MAIL_SIGNATURE_LIMITS,
  docForEdit,
  docForSave,
  docIsEmpty,
  type RichDoc,
} from '../lib/rich-doc';
import { buildDefaultGreeting } from '../lib/mail-signature-template';
import type { TotpEnrollStart, TrustedDevice } from '../lib/types';
import { oauthFailText } from '../lib/oauth-reason';
import { LoadingLines } from '../components/ui/Skeleton';
import { getPreferences, patchPreferences } from '../lib/preferences-api';
import { bellCounts } from '../lib/nav-api';

const ROLE_LABEL: Record<string, string> = {
  admin: '관리자 (admin)',
  mgmt: '경영지원 (mgmt)',
  sales: '영업부 (sales)',
  viewer: '조회 (viewer)',
};
const SECURITY_LEVEL_LABEL: Record<string, string> = {
  basic: '기본 (비밀번호만)',
  device: '신뢰 디바이스 (90일 OTP 면제)',
  mfa: '2단계 인증 (매번 OTP)',
};

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yy}-${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return iso;
  }
}

/**
 * 🔔 알림 — 포털챗봇 1:1 로 받는 알림 가운데 사람이 끌 수 있는 것 (v2.318.0 · Salesforce 기능·메뉴 검토 G3).
 *   월요일 아침 영업 요약(preferences.mondaySummary · 없으면 받음). viewer 는 영업건 담당이 아니라 절 자체가 없다.
 *   결재·개발요청 처리완료 알림은 업무 알림이라 여기서 끄지 않는다.
 */
function NotifySection({ role }: { role: string }) {
  const qc = useQueryClient();
  const prefsQ = useQuery({ queryKey: ['preferences'], queryFn: getPreferences, staleTime: 60_000, enabled: role !== 'viewer' });
  const patchMut = useMutation({
    mutationFn: patchPreferences,
    onSuccess: (p) => qc.setQueryData(['preferences'], p),
    onError: (e: Error) => alert(`저장 실패: ${e.message}`),
  });
  if (!['sales', 'mgmt', 'admin'].includes(role)) return null;
  const on = prefsQ.data?.mondaySummary !== false;
  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-1">🔔 알림</h2>
      <p className="text-xs text-slate-500 mb-3">웍스 「포털챗봇」 1:1 로 받는 알림 — 받고 싶지 않은 것을 끕니다. 결재·개발요청 처리완료 알림은 업무 알림이라 끄지 않습니다.</p>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          className="mt-1"
          checked={on}
          disabled={prefsQ.isLoading || patchMut.isPending}
          onChange={(e) => patchMut.mutate({ mondaySummary: e.target.checked })}
        />
        <span>
          <b>월요일 아침 영업 요약</b> — 매주 첫 영업일(월요일 · 쉬는 날이면 그 주 첫 영업일) 아침 8~12시에 내 진행 중 영업건 · 이번 주 납품예정 ·
          예정일 지난 건 · 2주 넘게 안 움직인 건 · 계산서 미발행을 한 통으로 받습니다.
          {role !== 'sales' ? ' 대표·부장에게는 팀 요약(담당자별 건수 · 지난 7일 변동)도 갑니다.' : ''}
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={prefsQ.data?.taskNotice !== false}
          disabled={prefsQ.isLoading || patchMut.isPending}
          onChange={(e) => patchMut.mutate({ taskNotice: e.target.checked })}
        />
        <span><b>할 일 아침 알림</b> — 일하는 날 아침 8~10시, 기한이 오늘이거나 지난 할 일이 있으면 한 통 (v2.319.0).</span>
      </label>
      <p className="text-xs text-slate-500 mt-2">포털챗봇과 연결돼 있어야 받습니다(연결이 없으면 보내지 않고 기록만 남깁니다). 보낼 내용이 하나도 없는 날·주에는 오지 않습니다.</p>
    </section>
  );
}

/**
 * 🔔 상단 알림 종에 띄울 항목 — 사람마다 켜고 끄기 (v2.323.0 · 개발요청 #34 한진영 · 사용자 결정 A안 「처음엔 전부 켜짐」).
 *   고를 수 있는 항목·숨긴 항목은 서버(GET /nav/bell · lib/nav/bell-items.ts)가 준다 — 화면에 항목 목록을 따로 적지 않는다.
 *   저장 = preferences.bellHidden(숨긴 key) · 다 켜면 키를 지운다. 「내 결재 대기」는 업무 알림이라 목록에 없다(끌 수 없음).
 *   종 창 아래 [표시 항목 고르기 →] 가 /mypage#bell 로 온다 — 그때 이 칸으로 내린다.
 */
function BellChoicesSection() {
  const qc = useQueryClient();
  const bellQ = useQuery({ queryKey: ['nav', 'bell'], queryFn: bellCounts, staleTime: 60_000 });
  const ref = useRef<HTMLElement>(null);
  const ready = !!bellQ.data;
  useEffect(() => {
    if (ready && window.location.hash === '#bell') ref.current?.scrollIntoView({ block: 'start' });
  }, [ready]);
  const patchMut = useMutation({
    mutationFn: (hidden: string[]) => patchPreferences({ bellHidden: hidden.length > 0 ? hidden : null }),
    onSuccess: (p) => {
      qc.setQueryData(['preferences'], p);
      void qc.invalidateQueries({ queryKey: ['nav', 'bell'] });
    },
    onError: (e: Error) => alert(`저장 실패: ${e.message}`),
  });
  const choices = bellQ.data?.choices ?? [];
  const hidden = patchMut.isPending && patchMut.variables ? patchMut.variables : (bellQ.data?.hidden ?? []);
  const toggle = (key: string, on: boolean): void => {
    const next = on ? hidden.filter((k) => k !== key) : [...hidden.filter((k) => k !== key), key];
    patchMut.mutate(next);
  };
  return (
    <section id="bell" ref={ref} className="bg-white rounded shadow-sm border border-slate-200 p-5 scroll-mt-14">
      <h2 className="ui-section-title mb-1">🔔 알림 종에 띄울 항목</h2>
      <p className="text-xs text-slate-500 mb-3">화면 위 🔔 를 눌렀을 때 보이는 항목 — 내 일과 상관없는 것은 끕니다. 끈 항목은 목록과 빨간 숫자에서 빠집니다. 「내 결재 대기」는 업무 알림이라 늘 보입니다.</p>
      {bellQ.isPending ? (
        <LoadingLines />
      ) : bellQ.isError ? (
        <p className="text-sm text-rose-600">항목을 불러오지 못했습니다 — 새로고침해 주세요</p>
      ) : (
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {choices.map((c) => (
            <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!hidden.includes(c.key)}
                disabled={patchMut.isPending}
                onChange={(e) => toggle(c.key, e.target.checked)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-500 mt-2">이 PC·폰 어디서나 같습니다(내 계정에 저장). 대시보드 알림 띠·웍스 아침 알림은 이 설정과 따로입니다.</p>
    </section>
  );
}

export function MyPage() {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();

  if (!user) {
    return null;
  }

  return (
    <div className="p-8 max-w-4xl">
      <h1 className="ui-page-title">마이페이지</h1>
      <p className="ui-page-sub">본인 계정 정보·보안 설정 관리</p>
      <LastUpdateBadge className="mb-6 mt-1" />

      <div className="space-y-5">
        <BasicInfoSection
          userId={user.userId}
          name={user.name ?? user.userId}
          role={user.role}
          securityLevel={user.securityLevel}
        />
        <SignatureSection />
        {/* 🔔 알림 — 월요일 아침 영업 요약 끄기/켜기 (v2.318.0 · 영업·경영지원·관리자) */}
        <NotifySection role={user.role} />
        {/* 🔔 알림 종에 띄울 항목 — 모든 계정 (v2.323.0 · #34) */}
        <BellChoicesSection />

        {/* ── 계정 연동 (v2.127.0) — 다른 시스템의 내 계정을 여기서 잇는다. 절마다 소속을 적어 두어
            나중에 서버가 갈릴 때 어느 절이 어느 서버 몫인지 화면이 말해 주게 한다. ── */}
        <div className="pt-2">
          <h2 className="ui-section-title border-b border-slate-200 pb-1 mb-3">🔗 계정 연동</h2>
          <p className="text-xs text-slate-500 -mt-1 mb-3">메일(SM 포털) · 일정(웍스 캘린더) · NAS(전사) · 기술지원포털(준비 중)</p>
        </div>
        {/* 메일도 각자의 웍스 메일함이라 **모든 직원**이 본인 계정을 연결한다 (v2.250.0 · 사용자 「메일 기능 뷰어 계정들도」
            — 전엔 viewer 를 감쌌다). 권한 조건으로 다시 감싸지 말 것 — 서버(routes/mail · MAIL_ROLES)도 전 직원이다. */}
        <MailAccountSection />
        {/* 일정은 **회사 공용 캘린더**라 조회 전용 계정도 본인 명의로 넣을 수 있어야 한다 —
            권한 조건으로 감싸지 말 것(v2.187.0 · 사용자 지시). */}
        <CalendarAccountSection />
        {/* 파일 탐색기는 포털 공용 계정이 아니라 각자의 NAS 계정으로 연다 —
            그래서 viewer 를 포함한 모든 직원이 본인 계정을 연결할 수 있어야 한다 (권한 조건으로 감싸지 말 것). */}
        <NasAccountSection />
        <section className="bg-white border border-dashed border-slate-300 rounded p-5">
          <h2 className="ui-section-title mb-1">기술지원포털 계정</h2>
          <p className="text-xs text-slate-500">
            준비 중 — 기술지원포털에 「통합 로그인 입장권」 창구가 붙으면 여기서 한 번 연결해 두고
            다시 로그인하지 않고 넘어갈 수 있게 됩니다. 그 전까지는 기술지원포털에서 따로 로그인합니다.
          </p>
        </section>

        {/* 메일 꼬리말 — 메일을 쓰는 사람 모두(v2.250.0 부터 viewer 포함 · 전엔 viewer 에게 안 보였다) */}
        <MailSignatureSection />
        <PasswordChangeSection />
        <OtpSection
          securityLevel={user.securityLevel}
          onChanged={() => {
            void refresh();
            // 등록·해제로 신뢰 기기가 늘거나 사라진다 — 아래 [신뢰된 디바이스] 목록도 같이 새로 읽는다.
            // (이게 없으면 방금 등록한 기기가 목록에 안 나와 "등록이 안 됐다"로 보인다)
            void qc.invalidateQueries({ queryKey: ['auth', 'devices'] });
          }}
        />
        <DevicesSection />
      </div>
    </div>
  );
}

// ── ① 본인 정보 ─────────────────────────────
function BasicInfoSection({
  userId,
  name,
  role,
  securityLevel,
}: {
  userId: string;
  name: string;
  role: string;
  securityLevel: string;
}) {
  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-3">본인 정보</h2>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
        <div>
          <dt className="text-xs text-slate-500">로그인 ID</dt>
          <dd className="font-mono font-medium">{userId}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">이름</dt>
          <dd className="font-medium">{name}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">권한</dt>
          <dd>{ROLE_LABEL[role] ?? role}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">보안 등급</dt>
          <dd>{SECURITY_LEVEL_LABEL[securityLevel] ?? securityLevel}</dd>
        </div>
      </dl>
    </section>
  );
}

// ── ② 서명 관리 (v1.5.0) ────────────────────
// 서명/직인 이미지 1장 (PNG/JPG ≤ 2MB) — 판매보고서 결재판의 본인 칸에 자동 표시.
function SignatureSection() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // 캐시 무효화 키 — 업로드/삭제 후 <img> 강제 갱신
  const [bust, setBust] = useState(1);

  const existsQuery = useQuery({
    queryKey: ['auth', 'mySignature'],
    queryFn: () => authApi.hasMySignature(),
    staleTime: 30_000,
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => authApi.uploadMySignature(file),
    onSuccess: () => {
      setFeedback({ kind: 'ok', text: '서명이 등록되었습니다. 판매보고서 결재판에 자동 반영됩니다.' });
      setBust((v) => v + 1);
      void qc.invalidateQueries({ queryKey: ['auth', 'mySignature'] });
    },
    onError: (e: Error) => setFeedback({ kind: 'err', text: e.message }),
  });

  const deleteMut = useMutation({
    mutationFn: () => authApi.deleteMySignature(),
    onSuccess: () => {
      setFeedback({ kind: 'ok', text: '서명이 삭제되었습니다.' });
      setBust((v) => v + 1);
      void qc.invalidateQueries({ queryKey: ['auth', 'mySignature'] });
    },
    onError: (e: Error) => setFeedback({ kind: 'err', text: e.message }),
  });

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    setFeedback(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일 재선택 허용
    if (!file) return;
    if (!/image\/(png|jpeg)/i.test(file.type)) {
      setFeedback({ kind: 'err', text: 'PNG 또는 JPG 이미지만 등록할 수 있습니다.' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setFeedback({ kind: 'err', text: '이미지 크기는 2MB 이하만 가능합니다.' });
      return;
    }
    uploadMut.mutate(file);
  }

  const hasSig = existsQuery.data === true;

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      {/* ⭐이름을 「서명 관리」에서 바꿨다 (v2.125.0) — 아래에 [메일 꼬리말] 이 생기면서
          「서명」이라는 말이 두 곳을 가리키게 됐다. 이쪽은 **결재에 찍히는 그림**이다. */}
      <h2 className="ui-section-title mb-1">결재 서명 (사인·직인)</h2>
      <p className="text-xs text-slate-500 mb-3 leading-snug">
        본인 사인·직인 이미지를 등록하면 <strong>판매보고서 결재판</strong>의 내 칸에 자동으로
        표시됩니다. (담당자 칸 = 영업건 담당자 서명 / 부서장·대표이사 칸 = 승인 시 서명)
        <br />배경이 투명한 PNG 를 권장합니다 (PNG·JPG, 2MB 이하).
      </p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-44 h-20 border border-dashed border-slate-300 rounded bg-slate-50 flex items-center justify-center overflow-hidden">
          {existsQuery.isLoading ? (
            <span className="text-xs text-slate-500">확인 중…</span>
          ) : hasSig ? (
            <img
              src={authApi.mySignatureUrl(bust)}
              alt="내 서명"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <span className="text-xs text-slate-500">등록된 서명 없음</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={handleFile}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadMut.isPending}
            className="btn btn-primary"
          >
            {uploadMut.isPending ? '업로드 중…' : hasSig ? '서명 교체' : '서명 등록'}
          </button>
          {hasSig && (
            <button
              type="button"
              onClick={() => {
                if (!window.confirm('등록된 서명을 삭제하시겠습니까?')) return;
                setFeedback(null);
                deleteMut.mutate();
              }}
              disabled={deleteMut.isPending}
              className="btn btn-danger"
            >
              삭제
            </button>
          )}
        </div>
      </div>
      {feedback && (
        <div
          className={`text-xs mt-3 ${feedback.kind === 'ok' ? 'text-emerald-600' : 'text-red-600'}`}
        >
          {feedback.text}
        </div>
      )}
    </section>
  );
}

// ── ③ 비밀번호 변경 ─────────────────────────
function PasswordChangeSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      authApi.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setFeedback('비밀번호가 변경되었습니다.');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    if (next.length < 8) {
      setFeedback('새 비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }
    if (next !== confirm) {
      setFeedback('새 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    mut.mutate();
  }

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-3">비밀번호 변경</h2>
      <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 text-sm">
        <div className="col-span-2">
          <label className="ui-label">현재 비밀번호</label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            className="ui-input w-full mt-1"
          />
        </div>
        <div>
          <label className="ui-label">새 비밀번호 (최소 8자)</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            className="ui-input w-full mt-1"
          />
        </div>
        <div>
          <label className="ui-label">새 비밀번호 확인</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            className="ui-input w-full mt-1"
          />
        </div>
        <div className="col-span-2 flex items-center justify-between">
          <div className="text-xs">
            {feedback && (
              <span
                className={
                  feedback.includes('변경되었습니다')
                    ? 'text-emerald-600'
                    : 'text-red-600'
                }
              >
                {feedback}
              </span>
            )}
            {mut.error && (
              <span className="text-red-600">
                {(mut.error as Error).message}
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={mut.isPending}
            className="btn btn-primary"
          >
            {mut.isPending ? '변경 중…' : '변경'}
          </button>
        </div>
      </form>
    </section>
  );
}

// ── ③ 2단계 인증 (OTP) ─────────────────────
function OtpSection({
  securityLevel,
  onChanged,
}: {
  securityLevel: string;
  onChanged: () => void;
}) {
  const enrolled = securityLevel !== 'basic';

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-3">2단계 인증 (OTP)</h2>
      {enrolled ? (
        <OtpEnrolledView securityLevel={securityLevel} onChanged={onChanged} />
      ) : (
        <OtpEnrollFlow onChanged={onChanged} />
      )}
    </section>
  );
}

function OtpEnrolledView({
  securityLevel,
  onChanged,
}: {
  securityLevel: string;
  onChanged: () => void;
}) {
  // 해제는 현재 비밀번호를 다시 확인한다 (v2.232.0 · 점검 ③ F-S4 — 열린 화면만으로 끌 수 있었다)
  const [pw, setPw] = useState('');
  const mut = useMutation({
    mutationFn: () => authApi.disableSecurity(pw),
    onSuccess: () => { setPw(''); onChanged(); },
  });

  function handleDisable() {
    if (!pw) return;
    if (
      !window.confirm(
        '2단계 인증을 해제하시겠습니까?\n등록된 모든 신뢰 디바이스도 함께 해제됩니다.',
      )
    )
      return;
    mut.mutate();
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-emerald-800">
        ✓ 2단계 인증이 등록되어 있습니다. 현재 보안 등급:{' '}
        <strong>{SECURITY_LEVEL_LABEL[securityLevel] ?? securityLevel}</strong>
      </div>
      <div className="text-xs text-slate-500 leading-snug">
        해제하면 비밀번호만으로 로그인이 가능해집니다 (보안 등급 → 기본). 모든 신뢰 디바이스도
        함께 해제됩니다. 다시 활성화하려면 아래 [등록] 단계를 처음부터 다시 진행해야 합니다.
      </div>
      {mut.error && (
        <div className="text-xs text-red-600">{(mut.error as Error).message}</div>
      )}
      <label className="block max-w-xs">
        <span className="text-xs text-slate-600">해제하려면 현재 비밀번호</span>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleDisable(); }}
          autoComplete="current-password"
          className="ui-input mt-1 w-full"
        />
      </label>
      <button
        type="button"
        onClick={handleDisable}
        disabled={mut.isPending || !pw}
        className={`btn ${mut.isPending
            ? 'border-slate-200 text-slate-400 cursor-wait'
            : !pw ? 'btn-secondary' : 'btn-danger'}`}
      >
        {mut.isPending ? '해제 중…' : '2단계 인증 해제'}
      </button>
    </div>
  );
}

function OtpEnrollFlow({ onChanged }: { onChanged: () => void }) {
  const [step, setStep] = useState<'idle' | 'showQr' | 'done'>('idle');
  const [enrollData, setEnrollData] = useState<TotpEnrollStart | null>(null);
  const [code, setCode] = useState('');
  const [securityLevel, setSecurityLevel] = useState<'device' | 'mfa'>('device');
  /** 등급이 '신뢰 디바이스' 일 때 지금 쓰는 기기에 붙일 이름. 비우면 서버가 브라우저 정보로 자동 (v2.57.2) */
  const [deviceName, setDeviceName] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const startMut = useMutation({
    mutationFn: () => authApi.totpEnrollStart(),
    onSuccess: (data) => {
      setEnrollData(data);
      setStep('showQr');
      setFeedback(null);
    },
  });
  const verifyMut = useMutation({
    mutationFn: () =>
      authApi.totpEnrollVerify({
        code,
        securityLevel,
        ...(securityLevel === 'device' && deviceName.trim()
          ? { deviceName: deviceName.trim() }
          : {}),
      }),
    onSuccess: () => {
      setStep('done');
      setEnrollData(null);
      setCode('');
      setDeviceName('');
      setFeedback(null);
      onChanged();
    },
  });

  if (step === 'idle') {
    return (
      <div className="space-y-3 text-sm">
        <div className="text-slate-600">
          현재 비밀번호만으로 로그인하고 있습니다. 2단계 인증을 등록하면 OTP 앱(예: Google
          Authenticator, 1Password)으로 추가 보안 인증을 받습니다.
        </div>
        {startMut.error && (
          <div className="text-xs text-red-600">
            {(startMut.error as Error).message}
          </div>
        )}
        <button
          type="button"
          onClick={() => startMut.mutate()}
          disabled={startMut.isPending}
          className="btn btn-primary"
        >
          {startMut.isPending ? '시작 중…' : '2단계 인증 등록'}
        </button>
      </div>
    );
  }

  if (step === 'showQr' && enrollData) {
    return (
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-5">
          {/* 좌: QR */}
          <div>
            <div className="text-xs text-slate-500 mb-2">
              ① OTP 앱(Google Authenticator 등)으로 아래 QR 스캔
            </div>
            <div className="bg-white border border-slate-200 rounded p-2 inline-block">
              <img
                src={enrollData.qrCodeDataUrl}
                alt="TOTP QR"
                className="w-48 h-48"
              />
            </div>
            <div className="text-xs text-slate-500 mt-2">
              QR 스캔이 안 되면 시크릿 직접 입력:
              <code className="block bg-slate-100 px-2 py-1 rounded mt-1 font-mono break-all">
                {enrollData.secret}
              </code>
            </div>
          </div>
          {/* 우: 복구 코드 */}
          <div>
            <div className="text-xs text-slate-500 mb-2">
              ② 복구 코드 (이번에만 표시 — 안전한 곳에 저장)
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 grid grid-cols-2 gap-1 font-mono text-xs">
              {enrollData.recoveryCodes.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </div>
            <div className="text-xs text-amber-700 mt-2 leading-snug">
              ⚠ OTP 앱을 잃어버렸을 때 사용하는 1회용 코드. 이 페이지를 닫으면 다시 볼 수 없습니다.
            </div>
          </div>
        </div>

        {/* 보안 등급 + 코드 검증 */}
        <fieldset className="border-t border-slate-200 pt-4">
          <div className="text-xs text-slate-500 mb-2">③ 보안 등급 선택</div>
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setSecurityLevel('device')}
              className={`ui-choice flex-1 ${securityLevel === 'device' ? 'ui-choice-on' : ''}`}
            >
              <div>신뢰 디바이스 (1단계)</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                현 디바이스 90일간 OTP 면제
              </div>
            </button>
            <button
              type="button"
              onClick={() => setSecurityLevel('mfa')}
              className={`ui-choice flex-1 ${securityLevel === 'mfa' ? 'ui-choice-on' : ''}`}
            >
              <div>2단계 인증 (강화)</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                로그인 시 매번 OTP 입력
              </div>
            </button>
          </div>

          {securityLevel === 'device' && (
            <div className="mb-3 border border-slate-200 rounded px-3 py-2 bg-slate-50">
              <label className="ui-label">
                이 기기의 이름 <span className="text-slate-400">(비우면 자동으로 붙습니다)</span>
                <input
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value.slice(0, 100))}
                  placeholder="예: 사무실 노트북"
                  className="ui-input block mt-1 w-full"
                />
              </label>
              <p className="text-xs text-slate-500 mt-1.5">
                코드 검증을 마치면 <strong>지금 쓰는 이 기기</strong>가 신뢰 기기로 등록되어
                90일간 OTP 없이 로그인됩니다. 아래 [신뢰된 디바이스] 목록에서 확인·해제할 수 있습니다.
              </p>
            </div>
          )}

          <div className="text-xs text-slate-500 mb-2">④ OTP 앱의 6자리 코드 입력</div>
          <div className="flex gap-2 items-center">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoFocus
              className="border border-slate-300 rounded px-3 py-1.5 font-mono text-lg w-32 tracking-widest text-center"
            />
            <button
              type="button"
              onClick={() => {
                if (code.length !== 6) {
                  setFeedback('6자리 코드를 입력하세요.');
                  return;
                }
                verifyMut.mutate();
              }}
              disabled={verifyMut.isPending || code.length !== 6}
              className="btn btn-primary"
            >
              {verifyMut.isPending ? '확인 중…' : '확인 + 등록'}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep('idle');
                setEnrollData(null);
                setCode('');
                setFeedback(null);
              }}
              className="btn btn-secondary"
            >
              취소
            </button>
          </div>
          {(feedback || verifyMut.error) && (
            <div className="text-xs text-red-600 mt-2">
              {feedback || (verifyMut.error as Error)?.message}
            </div>
          )}
        </fieldset>
      </div>
    );
  }

  // step === 'done'
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-emerald-800 text-sm">
      ✓ 2단계 인증이 등록되었습니다.
    </div>
  );
}

// ── ④ 신뢰된 디바이스 ───────────────────────
function DevicesSection() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['auth', 'devices'],
    queryFn: () => authApi.listDevices(),
    staleTime: 30_000,
  });
  const delMut = useMutation({
    mutationFn: (deviceId: string) => authApi.deleteDevice(deviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'devices'] });
    },
  });

  function handleDelete(d: TrustedDevice) {
    const label = d.deviceName ?? d.userAgent ?? d.deviceId.slice(0, 8);
    if (!window.confirm(`디바이스 [${label}] 을(를) 해제하시겠습니까?`)) return;
    delMut.mutate(d.deviceId);
  }

  const devices = query.data?.devices ?? [];

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="ui-section-title">신뢰된 디바이스</h2>
        <span className="text-xs text-slate-500">
          {query.isLoading ? '…' : `${devices.length}개`}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-snug">
        보안 등급이 <strong>신뢰 디바이스</strong>(1단계)인 경우, 등록된 디바이스에서 90일간 OTP
        없이 로그인할 수 있습니다. 분실된 기기나 모르는 디바이스가 있다면 즉시 해제하세요.
      </p>
      {query.isLoading && (
        <div className="text-center text-slate-500 text-xs py-6"><LoadingLines /></div>
      )}
      {query.isError && (
        <div className="text-center text-red-500 text-xs py-6">
          오류: {(query.error as Error).message}
        </div>
      )}
      {query.data && devices.length === 0 && (
        <div className="text-center text-slate-500 text-xs py-6 border border-dashed border-slate-200 rounded">
          등록된 신뢰 디바이스가 없습니다.
        </div>
      )}
      {devices.length > 0 && (
        <div className="overflow-x-auto">
          <table className="data-table w-full text-[13px]">
            <thead>
              <tr>
                <th className="text-left">디바이스</th>
                <th className="text-left">User Agent</th>
                <th className="text-left">최근 사용</th>
                <th className="text-left">만료</th>
                <th className="text-center w-20">작업</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.deviceId}>
                  <td className="font-medium">
                    {d.deviceName ?? <span className="text-slate-400">(이름 없음)</span>}
                  </td>
                  <td
                    className="text-slate-500 text-xs max-w-xs truncate"
                    title={d.userAgent ?? ''}
                  >
                    {d.userAgent ?? '-'}
                  </td>
                  <td className="text-slate-500 text-xs">
                    {fmtDateTime(d.lastUsedAt)}
                  </td>
                  <td className="text-slate-500 text-xs">
                    {fmtDateTime(d.expiresAt)}
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      onClick={() => handleDelete(d)}
                      disabled={delMut.isPending}
                      className="btn btn-danger btn-sm"
                    >
                      해제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {delMut.error && (
        <div className="text-xs text-red-600 mt-2">
          {(delMut.error as Error).message}
        </div>
      )}
    </section>
  );
}

// ── ②-2 일정 계정 (네이버웍스 캘린더) — v2.187.0 ────────────────
// 본인 웍스 계정을 연결하면 포털에서 넣는 일정의 **작성자가 본인**이 된다.
// ⭐넣는 곳은 언제나 **에스엠시스템즈 회사 캘린더** — 개인 캘린더로 가지 않는다.
// ⚠연결하지 않으면 일정은 **보기만** 된다(사용자 지시).
// 토큰은 서버에만(암호화) 저장 — 브라우저에는 내려오지 않는다.
function CalendarAccountSection() {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const account = useQuery({ queryKey: ['calendar', 'account'], queryFn: calApi.getCalendarAccount });

  // 콜백에서 되돌아온 결과 표시 (/mypage?cal=connected|error&reason=…) 후 주소 정리
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cal = params.get('cal');
    if (!cal) return;
    if (cal === 'connected') {
      setFeedback({ kind: 'ok', text: '네이버웍스 계정이 연결됐습니다 — 이제 일정을 본인 명의로 넣을 수 있습니다.' });
      void qc.invalidateQueries({ queryKey: ['calendar', 'account'] });
      void qc.invalidateQueries({ queryKey: ['works-calendar'] });
    } else {
      // 주소의 글자를 그대로 띄우지 않는다 — 정해진 사유 코드만 (v2.232.0 · 점검 ③ G-10 · lib/oauth-reason.ts)
      setFeedback({ kind: 'err', text: oauthFailText(params.get('reason')) });
    }
    params.delete('cal');
    params.delete('msg');
    params.delete('reason');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [qc]);

  const connect = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const { authorizeUrl } = await calApi.connectCalendarAccount();
      window.location.href = authorizeUrl; // 웍스 동의 화면 → 콜백이 여기로 돌아온다
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('일정 연결을 해제할까요? 해제하면 일정을 보기만 할 수 있습니다.')) return;
    setBusy(true);
    setFeedback(null);
    try {
      await calApi.disconnectCalendarAccount();
      setFeedback({ kind: 'ok', text: '연결을 해제했습니다.' });
      void qc.invalidateQueries({ queryKey: ['calendar', 'account'] });
      void qc.invalidateQueries({ queryKey: ['works-calendar'] });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const a = account.data;

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-1">일정 계정 (네이버웍스 캘린더)</h2>
      <p className="text-xs text-slate-500 mb-3">
        연결하면 포털에서 넣는 일정의 <b>작성자가 본인</b>이 됩니다. 일정은 <b>에스엠시스템즈 회사 캘린더</b>에
        들어가고 개인 캘린더에는 만들어지지 않습니다. <b>연결하지 않으면 일정은 보기만</b> 됩니다.
      </p>

      {feedback && (
        <div
          className={`mb-3 px-3 py-2 rounded text-sm border ${
            feedback.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {feedback.text}
        </div>
      )}

      {account.isLoading ? (
        <div className="text-sm text-slate-400">확인 중…</div>
      ) : !a?.configured ? (
        <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm">
          웍스 캘린더 연동이 아직 설정되지 않은 서버입니다. 관리자에게 문의해 주세요.
        </div>
      ) : a.connected ? (
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
              ● 연결됨
            </span>
            {a.nwEmail && <span className="text-sm font-medium text-slate-700">{a.nwEmail}</span>}
            <span className="text-xs text-slate-500">연결일 {fmtDateTime(a.connectedAt)}</span>
          </div>
          <button
            type="button"
            onClick={() => void disconnect()}
            disabled={busy}
            className="btn btn-secondary mt-3"
          >
            연결 해제
          </button>
        </div>
      ) : (
        <div>
          {a.expired && (
            <div className="mb-2 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
              연결이 만료됐습니다 — 다시 연결해 주세요.
            </div>
          )}
          <button
            type="button"
            onClick={() => void connect()}
            disabled={busy}
            className="btn btn-primary"
          >
            {a.expired ? '다시 연결' : '네이버웍스 계정 연결'}
          </button>
        </div>
      )}
    </section>
  );
}

// ── ②-1 메일 계정 (네이버웍스) — v2.108.0 ────────────────────
// 본인 웍스 계정을 OAuth 로 연결하면 포털 문서(견적서·발주서 등)를 메일로 바로 보낼 수 있다.
// 토큰은 서버에만(암호화) 저장 — 브라우저에는 내려오지 않는다.
function MailAccountSection() {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const account = useQuery({ queryKey: ['mail', 'account'], queryFn: mailApi.getMailAccount });
  const logs = useQuery({
    queryKey: ['mail', 'logs'],
    queryFn: mailApi.listMailLogs,
    enabled: showLogs && !!account.data?.connected,
  });

  // OAuth 콜백에서 되돌아온 결과 표시 (/mypage?mail=connected|error&reason=…) 후 URL 정리
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mail = params.get('mail');
    if (!mail) return;
    if (mail === 'connected') {
      setFeedback({ kind: 'ok', text: '네이버웍스 메일 계정이 연결됐습니다.' });
      void qc.invalidateQueries({ queryKey: ['mail', 'account'] });
    } else {
      // 주소의 글자를 그대로 띄우지 않는다 — 정해진 사유 코드만 (v2.232.0 · 점검 ③ G-10 · lib/oauth-reason.ts)
      setFeedback({ kind: 'err', text: oauthFailText(params.get('reason')) });
    }
    params.delete('mail');
    params.delete('msg');
    params.delete('reason');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [qc]);

  const connect = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const { authorizeUrl } = await mailApi.connectMailAccount();
      window.location.href = authorizeUrl; // 웍스 동의 화면으로 이동 → 콜백이 여기로 돌아온다
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('네이버웍스 메일 연결을 해제할까요?\n해제하면 포털에서 메일을 보낼 수 없습니다.')) return;
    setBusy(true);
    setFeedback(null);
    try {
      await mailApi.disconnectMailAccount();
      setFeedback({ kind: 'ok', text: '연결을 해제했습니다.' });
      void qc.invalidateQueries({ queryKey: ['mail', 'account'] });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const a = account.data;

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-1">메일 계정 (네이버웍스)</h2>
      <p className="text-xs text-slate-500 mb-3">
        {/* v2.250.0: 조회 전용 계정도 이 칸을 본다 — 견적서·발주서처럼 그 계정이 못 보내는 문서를 예로 들지 않는다 */}
        본인 웍스 계정을 연결하면 받은 메일을 포털에서 보고, 포털 문서를 본인 명의 메일로 바로 보낼 수 있습니다.
      </p>

      {feedback && (
        <div
          className={`mb-3 px-3 py-2 rounded text-sm border ${
            feedback.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {feedback.text}
        </div>
      )}

      {account.isLoading ? (
        <div className="text-sm text-slate-400">확인 중…</div>
      ) : !a?.configured ? (
        <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm">
          네이버웍스 메일 앱이 아직 등록되지 않았습니다. 관리자가 웍스 개발자 콘솔에 앱을 등록하면 이 자리에서 연결할 수 있습니다.
        </div>
      ) : a.connected ? (
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
                a.status === 'ACTIVE'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-rose-100 text-rose-700'
              }`}
            >
              {a.status === 'ACTIVE' ? '● 연결됨' : '● 만료됨 — 다시 연결 필요'}
            </span>
            <span className="text-sm font-medium text-slate-700">{a.nwEmail ?? '(메일 주소 미확인)'}</span>
            <span className="text-xs text-slate-500">연결일 {fmtDateTime(a.connectedAt)}</span>
          </div>
          {a.status === 'EXPIRED' && a.lastRefreshError && (
            <div className="mt-2 text-xs text-slate-500 break-all">사유: {a.lastRefreshError.slice(0, 200)}</div>
          )}
          <div className="flex items-center gap-2 mt-3">
            {a.status === 'EXPIRED' && (
              <button
                type="button"
                onClick={() => void connect()}
                disabled={busy}
                className="btn btn-primary"
              >
                다시 연결
              </button>
            )}
            <button
              type="button"
              onClick={() => void disconnect()}
              disabled={busy}
              className="btn btn-secondary"
            >
              연결 해제
            </button>
            <button
              type="button"
              onClick={() => setShowLogs((v) => !v)}
              className="btn btn-ghost"
            >
              {showLogs ? '발송 기록 접기 ▴' : '발송 기록 보기 ▾'}
            </button>
          </div>
          {showLogs && (
            <div className="mt-3 border-t border-slate-100 pt-2">
              {logs.isLoading ? (
                <div className="text-xs text-slate-500 py-2"><LoadingLines /></div>
              ) : !logs.data?.logs.length ? (
                <div className="text-xs text-slate-500 py-2">아직 발송 기록이 없습니다.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-400">
                      <th className="py-1 pr-2 font-medium">보낸 때</th>
                      <th className="py-1 pr-2 font-medium">받는사람</th>
                      <th className="py-1 pr-2 font-medium">제목</th>
                      <th className="py-1 pr-2 font-medium">첨부</th>
                      <th className="py-1 font-medium">결과</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.data.logs.slice(0, 10).map((l) => (
                      <tr key={l.id} className="border-t border-slate-100 align-top">
                        <td className="py-1 pr-2 whitespace-nowrap text-slate-500">{fmtDateTime(l.created_at)}</td>
                        <td className="py-1 pr-2 max-w-[18ch] truncate" title={l.to_addrs}>{l.to_addrs}</td>
                        <td className="py-1 pr-2 max-w-[24ch] truncate" title={l.subject}>{l.subject}</td>
                        <td className="py-1 pr-2 whitespace-nowrap text-slate-500">
                          {Array.isArray(l.attachments) && l.attachments.length ? `${l.attachments.length}개` : '-'}
                        </td>
                        <td className="py-1 whitespace-nowrap">
                          {l.status === 'sent' ? (
                            <span className="text-emerald-600">성공</span>
                          ) : (
                            <span className="text-rose-600" title={l.error ?? ''}>실패</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <p className="text-sm text-slate-600 mb-3">
            아직 연결돼 있지 않습니다. [연결하기]를 누르면 네이버웍스 로그인·동의 화면으로 이동합니다.
          </p>
          <button
            type="button"
            onClick={() => void connect()}
            disabled={busy}
            className="btn btn-success-solid"
          >
            {busy ? '이동 중…' : '네이버웍스 계정 연결하기'}
          </button>
        </div>
      )}
    </section>
  );
}

// ── ②-1-1 메일 꼬리말 (v2.125.0 · 사용자 요청) ────────────────────
// 「메일에 고정으로 들어갈 서명 등록하는 메뉴를 마이페이지에.」
// ⛔위의 [결재 서명 (사인·직인)] 과 **다른 것**이다 — 그쪽은 결재판에 찍히는 그림 한 장,
//   이쪽은 대외 메일 맨 아래에 붙는 서식 있는 글 덩어리다. 이름을 갈라 둔 이유가 그것이다.
// ⭐⭐⭐저장되는 것은 HTML 이 아니라 **문서 나무**다(v2.124.0 원칙) — 서버가 허용 목록으로
//   거른 뒤 담고, 메일 HTML 은 보낼 때 서버가 우리 태그만으로 새로 짓는다.
// ⭐⭐⭐**두 부분이다 (v2.211.0 · 사용자 「인사말 맨트는 공통적용 아니고, 꼬리말 중 회사정보 부분만 공통으로」)**
//   ① 인사말 — 여기 편집기에서 각자 적고 저장한다(비워도 된다).
//   ② 회사 정보 표 — 전 직원 공통 · **저장하지 않는다.** 메일 창을 열 때마다 직원 정보(이름·직급·부서·
//      휴대폰·메일)로 새로 짓는다. 직급·휴대폰은 관리자가 [시스템 관리 › 사용자 관리]에서 고친다(사용자 결정).
//   아래 미리보기는 지금 내 직원 정보로 지은 그 표를 **읽기 전용**으로 보여 준다.
function MailSignatureSection() {
  const qc = useQueryClient();
  const [doc, setDoc] = useState<RichDoc>(EMPTY_DOC);
  const [enabled, setEnabled] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // 인사말(개인 부분) + 회사 정보 표(내 직원 정보로 지어 둔 것) — 메일 창과 같은 조회·같은 캐시
  const sig = useQuery({ queryKey: ['mail', 'signature'], queryFn: mailApi.getMailSignature });
  const { user } = useAuth();
  const profile = sig.data?.profile ?? null;
  const companyBlock = sig.data?.companyBlock ?? [];

  /**
   * [기본 인사말 넣기] — 예전 [웍스 서명 모양 넣기] 가 넣던 인사말 문장을 편집기에 넣는다(이름 뒤 직급).
   * 회사 정보 표는 이제 메일 창이 알아서 붙이므로 여기에는 인사말만 넣는다.
   * ⚠**덮어쓰기**다. 쓰던 것이 있으면 먼저 물어본다 — 되돌릴 길이 없다(저장 전이면 새로고침).
   */
  const fillGreeting = (): void => {
    if (!profile) return;
    if (!docIsEmpty(doc) && !window.confirm(
      '지금 적혀 있는 인사말을 기본 인사말로 바꿉니다.\n\n지금 내용은 사라집니다. 계속할까요?',
    )) return;
    setDoc({ type: 'doc', content: buildDefaultGreeting(profile) });
    setDirty(true);
    setFeedback({ kind: 'ok', text: '기본 인사말을 넣었습니다. 고친 뒤 [저장] 을 눌러야 실제로 적용됩니다.' });
  };

  // 서버에서 읽은 것을 편집기에 넣는다 — 저장된 그림 번호에 볼 수 있는 주소를 붙여서.
  // ⚠아직 저장 안 한 내용이 있으면 덮어쓰지 않는다 (다른 창에서 새로 읽혀 글이 날아가는 것 방지)
  // ⚠deps 에 **객체를 넣지 않는다** — 조회가 다시 돌면 같은 내용이라도 새 물건이 와서
  //   편집기를 통째로 갈아 끼우고 커서가 맨 앞으로 튄다. 값(저장 시각)으로 본다.
  const loaded = sig.data;
  const loadedStamp = sig.isSuccess ? String(loaded?.updatedAt ?? 'none') : null;
  useEffect(() => {
    if (!loaded || dirty) return;
    setDoc(docForEdit(loaded.doc, mailApi.mailSignatureImageUrl));
    setEnabled(loaded.enabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedStamp]);

  const saveMut = useMutation({
    mutationFn: () => mailApi.saveMailSignature(docForSave(doc), enabled),
    onSuccess: (saved) => {
      setDirty(false);
      setDoc(docForEdit(saved.doc, mailApi.mailSignatureImageUrl));
      setEnabled(saved.enabled);
      setFeedback({ kind: 'ok', text: '꼬리말을 저장했습니다. 다음에 메일 창을 열면 본문 끝에 인사말과 회사 정보가 들어갑니다.' });
      void qc.invalidateQueries({ queryKey: ['mail', 'signature'] });
    },
    onError: (e: Error) => setFeedback({ kind: 'err', text: e.message }),
  });

  const deleteMut = useMutation({
    mutationFn: () => mailApi.deleteMailSignature(),
    onSuccess: () => {
      setDirty(false);
      setDoc(EMPTY_DOC);
      setEnabled(true);
      setFeedback({ kind: 'ok', text: '인사말을 삭제했습니다. 회사 정보 칸은 그대로 붙습니다.' });
      void qc.invalidateQueries({ queryKey: ['mail', 'signature'] });
    },
    onError: (e: Error) => setFeedback({ kind: 'err', text: e.message }),
  });

  const busy = saveMut.isPending || deleteMut.isPending;
  const registered = !!loaded && docIsEmpty(loaded.doc) === false;
  // ⚠편집기는 value 가 「다른 물건」이면 내용을 통째로 다시 그린다 — 조회 결과가 같으면 같은 물건을 넘긴다
  const companyDoc = useMemo<RichDoc>(() => ({ type: 'doc', content: companyBlock }), [companyBlock]);

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-1">메일 꼬리말</h2>
      <p className="text-xs text-slate-500 mb-3 leading-snug">
        포털에서 보내는 <strong>모든 메일 본문 맨 아래</strong>에 자동으로 들어갑니다 —
        <strong> ① 내 인사말</strong> + <strong>② 회사 정보(전 직원 공통)</strong>.
        메일 창에서 그 메일에만 고쳐 쓰거나 지울 수 있습니다.
        {/* ⛔「위의 [결재 서명] 과는 다른 것입니다」 안내를 두었다가 뺐다 (v2.125.1 · 사용자 판단) —
            칸 이름이 [메일 꼬리말] 이라 결재 서명과 헷갈릴 일이 없어 설명이 군더더기였다.
            ⚠다시 넣지 말 것. 이름을 바꿔 혼동이 생기면 그때는 **안내가 아니라 이름**을 고칠 자리다. */}
      </p>

      {feedback && (
        <div
          className={`mb-3 px-3 py-2 rounded text-sm border ${
            feedback.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {feedback.text}
        </div>
      )}

      {sig.isLoading ? (
        <div className="text-sm text-slate-400"><LoadingLines /></div>
      ) : (
        <>
          <div className="text-sm font-semibold text-slate-600 mb-1">
            ① 내 인사말
            <span className="ml-2 text-xs font-normal text-slate-500">사람마다 다름 · 비워 두면 회사 정보만 붙습니다</span>
          </div>
          <RichEditor
            label="메일 꼬리말 — 내 인사말"
            value={doc}
            onChange={(next) => {
              setDoc(next);
              setDirty(true);
            }}
            limits={MAIL_SIGNATURE_LIMITS}
            minHeight={110}
            placeholder={'안녕하세요. 에스엠시스템즈 홍길동 입니다.\n감사합니다.'}
          />
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setFeedback(null);
                saveMut.mutate();
              }}
              disabled={busy}
              className="btn btn-primary"
            >
              {saveMut.isPending ? '저장 중…' : '저장'}
            </button>
            <button
              type="button"
              onClick={fillGreeting}
              disabled={busy || !profile}
              title="「안녕하세요. 에스엠시스템즈 (이름 직급) 입니다.」로 시작하는 기본 인사말을 넣습니다"
              className="btn btn-secondary"
            >
              기본 인사말 넣기
            </button>
            {registered && (
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm('적어 둔 인사말을 지울까요?\n회사 정보 칸은 그대로 붙습니다.')) return;
                  setFeedback(null);
                  deleteMut.mutate();
                }}
                disabled={busy}
                className="btn btn-danger"
              >
                인사말 삭제
              </button>
            )}
            <label className="flex items-center gap-1.5 text-sm text-slate-600 ml-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked);
                  setDirty(true);
                }}
              />
              메일 창에 꼬리말 자동으로 넣기 (인사말 + 회사 정보)
            </label>
            <span className="ml-auto text-xs text-slate-500">
              {dirty
                ? '아직 저장하지 않은 변경이 있습니다'
                : loaded?.updatedAt
                  ? `마지막 저장 ${fmtDateTime(loaded.updatedAt)}`
                  : '아직 저장한 인사말이 없습니다'}
            </span>
          </div>

          {/* ② 회사 정보 — 전 직원 공통 · 저장하지 않는다(메일 창이 매번 직원 정보로 짓는다) · 여기는 읽기 전용 미리보기 */}
          <div className="mt-5">
            <div className="text-sm font-semibold text-slate-600 mb-1">
              ② 회사 정보
              <span className="ml-2 text-xs font-normal text-slate-500">전 직원 공통 · 자동</span>
            </div>
            <p className="text-xs text-slate-500 mb-2 leading-snug">
              이름·직급·부서·휴대폰은 직원 정보에서 자동으로 들어갑니다
              {user?.role === 'admin'
                ? ' — [시스템 관리 › 사용자 관리] 에서 고칩니다.'
                : ' — 바꿀 것이 있으면 관리자에게 말씀해 주세요.'}
              {profile && !profile.mobile && (
                <span className="text-amber-700"> 휴대폰 번호가 비어 있어 M 줄 없이 나갑니다.</span>
              )}
            </p>
            {companyBlock.length > 0 ? (
              <RichEditor
                label="메일 꼬리말 — 회사 정보 (미리보기)"
                value={companyDoc}
                onChange={() => { /* 읽기 전용 */ }}
                readOnly
                limits={MAIL_SIGNATURE_LIMITS}
                minHeight={60}
              />
            ) : (
              <div className="text-sm text-slate-400">회사 정보를 불러오지 못했습니다 — 새로고침해 주세요.</div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

// ── ②-2 NAS 계정 ────────────────────
// 파일 탐색기는 포털 공용 계정이 아니라 「그 사람의 NAS 계정」으로 연다 — 보이는 폴더 범위를
// NAS 쪽 권한이 그대로 정하게 하려는 것이다. 그래서 메일과 달리 OAuth 가 아니라 계정/비밀번호를 받는다.
// 비밀번호는 서버에서 암호화 보관하고 화면으로는 절대 되돌려 보내지 않는다 — 여기서도 성공·실패 후 즉시 비운다.
function NasAccountSection() {
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // 이미 연결된 상태에서 [다시 연결] 을 누르면 자격증명 칸을 다시 연다 (비밀번호 변경·만료 대응)
  const [reconnecting, setReconnecting] = useState(false);

  const account = useQuery({ queryKey: ['nas', 'account'], queryFn: fetchNasAccount });

  const connectMut = useMutation({
    mutationFn: () => connectNasAccount(username.trim(), password),
    onSuccess: (data) => {
      setPassword('');
      setReconnecting(false);
      setFeedback({
        kind: 'ok',
        text: `NAS 계정 [${data.nasUsername ?? username.trim()}] 을(를) 연결했습니다.`,
      });
      void qc.invalidateQueries({ queryKey: ['nas', 'account'] });
    },
    onError: (e: Error) => {
      // 로그인 실패면 서버가 아무것도 저장하지 않는다 — 오타 재입력을 유도하기 위해 비밀번호만 비운다.
      setPassword('');
      setFeedback({ kind: 'err', text: e.message });
    },
  });

  const disconnectMut = useMutation({
    mutationFn: () => disconnectNasAccount(),
    onSuccess: () => {
      setUsername('');
      setPassword('');
      setReconnecting(false);
      setFeedback({ kind: 'ok', text: '연결을 해제했습니다.' });
      void qc.invalidateQueries({ queryKey: ['nas', 'account'] });
    },
    onError: (e: Error) => setFeedback({ kind: 'err', text: e.message }),
  });

  function handleDisconnect() {
    if (
      !window.confirm(
        'NAS 계정 연결을 해제할까요?\n해제하면 [NAS 파일탐색기]에서 NAS 폴더를 열 수 없습니다.',
      )
    )
      return;
    setFeedback(null);
    disconnectMut.mutate();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    connectMut.mutate();
  }

  const a = account.data;
  const busy = connectMut.isPending || disconnectMut.isPending;
  // 연결 전이거나, 연결돼 있어도 [다시 연결] 을 눌렀을 때만 입력칸을 보인다.
  const showForm = !a?.connected || reconnecting;
  const canSubmit = !!username.trim() && !!password && !busy;

  return (
    <section className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <h2 className="ui-section-title mb-1">NAS 계정 연결</h2>
      <p className="text-xs text-slate-500 mb-3">
        본인 NAS 계정을 연결하면 포털의 [NAS 파일탐색기]에서 NAS 폴더를 열람·업로드할 수 있습니다.
        보이는 폴더 범위는 NAS 에 설정된 <strong>본인 계정 권한</strong> 그대로입니다.
      </p>

      {feedback && (
        <div
          className={`mb-3 px-3 py-2 rounded text-sm border ${
            feedback.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {feedback.text}
        </div>
      )}

      {account.isLoading ? (
        <div className="text-sm text-slate-400">확인 중…</div>
      ) : account.isError ? (
        <div className="text-sm text-rose-600">
          연결 상태를 읽지 못했습니다: {(account.error as Error).message}
        </div>
      ) : a && !a.ready ? (
        // 서버가 NAS 를 아예 못 쓰는 상태 — 자격증명을 받아 봐야 실패하므로 입력칸 대신 사유만 보인다.
        <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm">
          {a.readyReason ?? 'NAS 연결이 아직 준비되지 않았습니다. 관리자에게 문의해 주세요.'}
        </div>
      ) : (
        <div>
          {a?.connected && (
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
                    a.status === 'ACTIVE'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-rose-100 text-rose-700'
                  }`}
                >
                  {a.status === 'ACTIVE' ? '● 연결됨' : '● 다시 연결 필요'}
                </span>
                <span className="text-sm font-medium text-slate-700">
                  {a.nasUsername ?? '(계정 미확인)'}
                </span>
                <span className="text-xs text-slate-500">연결일 {fmtDateTime(a.connectedAt)}</span>
                <span className="text-xs text-slate-500">
                  최근 확인 {fmtDateTime(a.lastVerifiedAt)}
                </span>
              </div>
              {a.status === 'ERROR' && a.lastError && (
                <div className="mt-2 text-xs text-slate-500 break-all">
                  사유: {a.lastError.slice(0, 200)}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => {
                    setFeedback(null);
                    setPassword('');
                    setUsername(a.nasUsername ?? '');
                    setReconnecting((v) => !v);
                  }}
                  disabled={busy}
                  className="btn btn-primary"
                >
                  {reconnecting ? '다시 연결 접기' : '다시 연결'}
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={busy}
                  className="btn btn-secondary"
                >
                  {disconnectMut.isPending ? '해제 중…' : '연결 해제'}
                </button>
              </div>
            </div>
          )}

          {showForm && (
            <form
              onSubmit={handleSubmit}
              className={a?.connected ? 'mt-3 border-t border-slate-100 pt-3' : ''}
            >
              {!a?.connected && (
                <p className="text-sm text-slate-600 mb-3">
                  아직 연결돼 있지 않습니다. NAS 로그인에 쓰는 계정과 비밀번호를 입력해 주세요.
                </p>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm max-w-lg">
                <div>
                  <label className="ui-label">NAS 계정</label>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    placeholder="NAS 로그인 ID"
                    className="ui-input w-full mt-1"
                  />
                </div>
                <div>
                  <label className="ui-label">비밀번호</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="ui-input w-full mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2 leading-snug">
                비밀번호는 서버에 <strong>암호화되어</strong> 저장되며, 본인 명의로 NAS 를 여는
                용도로만 쓰입니다. 저장 후에는 화면에 다시 표시되지 않습니다.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn btn-success-solid"
                >
                  {connectMut.isPending ? '연결 중…' : '연결'}
                </button>
                {a?.connected && (
                  <button
                    type="button"
                    onClick={() => {
                      setReconnecting(false);
                      setPassword('');
                    }}
                    className="btn btn-secondary"
                  >
                    취소
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
